import {
  AnchoredPopup,
  CloseIcon,
  IconButton,
  MultiSelectDropdown,
  RotateCcwIcon,
  SingleSelectDropdown,
  useAnchoredPopupAnchor,
  type MultiSelectDropdownOption,
  type SingleSelectDropdownOption,
} from "@carbon6/c6-frontend-design";
import {
  useEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from "react";
import {
  EPISODE_LIST_FILTER_KEYS,
  getDatasetEpisodeFacets,
  type EpisodeFacets,
  type EpisodeListFilters as Filters,
} from "@/api";
import { hatcheryDropdownModes } from "@/app/theme";
import { FilterBar, FilterSearchInput, FilterSheetButton } from "@/components/filter";
import { normalizeTagSelection } from "@/components/label";
import {
  applyFreezeRange,
  FREEZE_AXIS_LABEL,
  freezeRangeOptions,
  pendingLongFreeze,
  readFreezeRange,
} from "./episodeFreezeFilter";
import { useLabelCategories } from "@/components/label/useLabelCategories";
import {
  applyBandAxisValue,
  applyEpisodeFilterAxis,
  applyEpisodeTagAxis,
  BAND_CHANNEL_AXIS_LABEL,
  episodeBandFilterAxes,
  episodeBandUnmeasuredNote,
  episodeFilterChips,
  episodeQualityCandidateAxis,
  episodeReviewFilterAxes,
  episodeSheetFilterCount,
  episodeTagFilterAxes,
  episodeWorstChannelOptions,
  EPISODE_FILTER_ALL,
  readEpisodeFilterAxis,
} from "./episodeFilterAxes";
import { withoutDuration } from "./episodeDurationFilter";
import { EpisodeDurationHistogram } from "./EpisodeDurationHistogram";
import { useEpisodeReviewSavedRevision } from "./episodeReviewSavedStore";
import styles from "./EpisodeFilterLane.module.css";

// 팝오버가 열린 상태에서 조건이 바뀌면 개수를 다시 받기 전에 이 시간(ms)만큼 기다린다.
// 다중 선택을 연달아 체크하는 동안 요청이 체크 수만큼 늘지 않게 묶는다.
// 값은 목록 필터 커밋과 같다.
const FACETS_REFETCH_DEBOUNCE_MS = 300;

// 태그 축 드롭다운에서 검색을 켜는 선택지 수의 하한. 데이터셋 필터(`FacetControl`)와 같은 임계값이다.
const SEARCHABLE_OPTION_THRESHOLD = 8;

/**
 * 에피소드 목록 필터 레인. 좁은 목록 열에 놓는 §17 필터 바다.
 *
 * 행 1 에는 **이름 검색 + `⚙ 필터`** 만 있다. 나머지 축은 모두 그 버튼이 여는 팝오버 안에 있다.
 * 이 레인의 폭은 200~236px 이다. 축을 모두 한 줄에 나열하면 목록을 놓을 공간이 없다.
 * 또 트리거가 글자를 잘라서 무엇을 고르는 칸인지 보이지 않는다.
 * 행 2 의 조건 칩이 팝오버 안의 조건을 표시한다. `↺ 초기화` 는 검색까지 한 번에 비운다.
 *
 * **두 표면이 이 컴포넌트를 같이 쓴다.**
 *   - 데이터셋 상세의 에피소드/파일 탭
 *   - 학습 제출 창의 미리보기
 * 두 표면은 폭 예산이 거의 같다. 리뷰어는 실제로 두 표면을 오가며 같은 조건을 적용한다.
 * 배치가 다르면 사용자가 매번 다시 배워야 한다. 필터 상태는 각 화면이 로컬로 소유하고
 * URL 에 두지 않는다. 학습 제출 창은 위저드 단계라 URL 축이 없다.
 *
 * 필터는 서버 질의이므로 total 과 페이지가 함께 줄어든다. 각 축은 단일값이고 기본값은
 * "전체"(값 없음)다. 서버 파라미터에 부정형이 없으므로 "제외됨 아님" 같은 조건은 만들 수 없다.
 * 제외된 에피소드의 기본 숨김은 목록 헤더의 눈 토글이 소유한다.
 */
export type EpisodeFilterLaneProps = {
  // 개수를 조회할 데이터셋. seam(`FileBrowserSource`)의 resource 는 현재 dataset 하나뿐이다.
  // 그래서 두 표면 모두 그 uuid 를 그대로 전달한다. dataset 이 아닌 소스가 생기면 그때 이 prop 을
  // optional 로 바꾸고 수를 숨긴다. 지금 seam 에 facets 멤버를 미리 두면 사용처 없는 추상이 된다(§6).
  datasetUuid: string;
  value: Filters;
  onChange: (next: Filters) => void;
  disabled?: boolean;
};

export function EpisodeFilterLane({
  datasetUuid,
  value,
  onChange,
  disabled = false,
}: EpisodeFilterLaneProps): ReactNode {
  const popup = useAnchoredPopupAnchor();
  const [open, setOpen] = useState(false);
  const facets = useEpisodeFacets(datasetUuid, value, open);
  const membership = useQualityMembership(open);

  const chips = episodeFilterChips(value, facets).map((chip) => ({
    key: chip.key,
    label: chip.label,
    removeLabel: `${chip.label} 해제`,
    onRemove: () => onChange(chip.next),
  }));

  // 트리거는 토글이다. c6 는 트리거를 누르는 것을 바깥 누름으로 보지 않는다. 그래서 같은 버튼으로
  // 닫는 동작은 여기서 한다.
  function toggleSheet(event: ReactMouseEvent<HTMLElement>): void {
    if (open) {
      closeSheet();
      return;
    }
    popup.openFromElement(event);
    setOpen(true);
  }

  function closeSheet(): void {
    setOpen(false);
    popup.close();
  }

  const hasAnyFilter = Object.keys(value).length > 0;
  const bandNote = episodeBandUnmeasuredNote(facets);
  const candidateAxis = episodeQualityCandidateAxis(facets);
  const shortDurationSelected = value.duration_min === 0 && value.duration_max === 3;

  return (
    <>
      <FilterBar
        lane="narrow"
        ariaLabel="에피소드 필터"
        search={
          <FilterSearchInput
            value={value.search ?? ""}
            ariaLabel="에피소드 이름 검색"
            placeholder="이름 일부를 입력하세요"
            disabled={disabled}
            onCommit={(next) => onChange(applyEpisodeFilterAxis(value, "search", next))}
          />
        }
        chips={chips}
        onClearAll={() => onChange({})}
      >
        <FilterSheetButton
          label="필터"
          count={episodeSheetFilterCount(value)}
          open={open}
          disabled={disabled}
          onOpen={toggleSheet}
        />
      </FilterBar>
      <AnchoredPopup
        open={open}
        anchor={popup.anchor}
        onClose={closeSheet}
        placement="bottom-start"
        role="dialog"
        aria-label="에피소드 필터"
        width="30rem"
        maxHeight="min(70vh, 34rem)"
      >
        <AnchoredPopup.Header divider className={styles.header}>
          <span className={styles.title}>에피소드 필터</span>
          <IconButton aria-label="필터 닫기" size="sm" variant="surface" onClick={closeSheet}>
            <CloseIcon />
          </IconButton>
        </AnchoredPopup.Header>
        <AnchoredPopup.Body padding="md">
          {/* 개수의 모집단을 한 번만 알린다. 축마다 툴팁을 붙이면 좁은 팝오버에서 같은 문구가
              반복되고 서로 겹친다. 수가 하나도 없을 때 규약만 설명하면 틀린 안내가 된다.
              그래서 facets 를 받았을 때만 표시한다. */}
          {facets !== null && (
            <p className={styles.caption}>
              선택지 옆 수는 다른 조건은 그대로 두고 그 값 하나로 좁혔을 때의 에피소드
              수입니다(제외된 에피소드 포함).
            </p>
          )}
          <div className={styles.axes}>
            <div className={styles.groupBreak}>
              <span>검토 · 기록</span>
            </div>
            {episodeReviewFilterAxes(facets).map((axis) => (
              <div key={axis.key} className={styles.axis}>
                <SingleSelectDropdown
                  mode={hatcheryDropdownModes.filter}
                  label={axis.label}
                  // 격자 칸을 채운다. c6 기본은 content-width 라 값 길이에 따라 트리거 폭이 달라진다.
                  // 2열 격자에서 폭이 다르면 정렬이 어긋나 보인다.
                  fullWidth
                  neutralValue={EPISODE_FILTER_ALL}
                  options={axis.options as readonly SingleSelectDropdownOption[]}
                  value={readEpisodeFilterAxis(value, axis.key)}
                  disabled={disabled}
                  onValueChange={(next) => onChange(applyEpisodeFilterAxis(value, axis.key, next))}
                />
              </div>
            ))}
            {/* 태그 축은 한 줄 전체를 쓴다. 갈래와 값 문구가 `한글(code)` 형태라 2열의 반 칸
                (약 12rem)에서 잘린다. 폭은 className 이 아니라 래퍼가 정한다(c6 컨트롤 규약). */}
            {episodeTagFilterAxes(value, facets, membership).map((axis) => (
              <div key={axis.key} className={styles.wide}>
                <MultiSelectDropdown
                  mode={hatcheryDropdownModes.filter}
                  label={axis.label}
                  placeholder={axis.placeholder}
                  fullWidth
                  options={axis.options as readonly MultiSelectDropdownOption[]}
                  menuWidth={axis.key === "quality" ? "26rem" : undefined}
                  maxMenuHeight={axis.key === "quality" ? "min(70vh, 32rem)" : undefined}
                  selectedValues={axis.selected}
                  closeOnSelect={false}
                  searchable={axis.options.length >= SEARCHABLE_OPTION_THRESHOLD}
                  searchPlaceholder="검색"
                  clearSelectionLabel="선택 해제"
                  disabled={disabled}
                  onSelectionChange={(next) =>
                    onChange(
                      applyEpisodeTagAxis(
                        value,
                        axis.key,
                        axis.key === "quality" ? normalizeTagSelection(axis.options, next) : next,
                      ),
                    )
                  }
                />
              </div>
            ))}
            {/* 계산값과 판정을 실선으로 구분한다. 붙여 두면 같은 종류로 읽힌다.
                플랫폼은 어느 데이터가 더 낫다고 판정하지 않는다(진단 spec D1). */}
            <section className={styles.candidateCard} aria-label="자동 품질 후보">
              <h3 className={styles.candidateTitle}>자동 품질 후보</h3>
              <p className={styles.axisNote}>
                자동 분석의 제안입니다. 기록된 결함과 별도로 고릅니다.
              </p>
              {/* `영상 정지 후보: 전체 (N)` 이 반 칸에서 잘리므로 한 줄을 쓴다. */}
              <div className={styles.wide}>
                <SingleSelectDropdown
                  mode={hatcheryDropdownModes.filter}
                  label={candidateAxis.label}
                  fullWidth
                  neutralValue={EPISODE_FILTER_ALL}
                  options={candidateAxis.options as readonly SingleSelectDropdownOption[]}
                  value={readEpisodeFilterAxis(value, candidateAxis.key)}
                  disabled={disabled}
                  onValueChange={(next) =>
                    onChange(applyEpisodeFilterAxis(value, candidateAxis.key, next))
                  }
                />
              </div>
              <SingleSelectDropdown
                mode={hatcheryDropdownModes.filter}
                label={FREEZE_AXIS_LABEL}
                fullWidth
                neutralValue=""
                options={freezeRangeOptions(value, facets)}
                value={readFreezeRange(value)}
                disabled={disabled || facets?.freeze_longest === undefined}
                onValueChange={(next) => onChange(applyFreezeRange(value, next))}
              />
              <IconButton
                text="미처리 + 30초 이상"
                type="button"
                variant="surface"
                size="sm"
                disabled={disabled || facets?.freeze_longest === undefined}
                onClick={() => onChange(pendingLongFreeze(value))}
              />
              <p className={styles.axisNote}>
                길이는 처리 상태와 독립입니다. 후보가 없거나 미분석인 에피소드는 길이 조건에
                포함되지 않습니다.
              </p>
            </section>
            <div className={styles.groupBreak}>
              <span>기타 측정값</span>
            </div>
            <div className={styles.wide}>
              <IconButton
                text="3초 미만"
                type="button"
                variant="surface"
                tone="primary"
                selected={shortDurationSelected}
                disabled={disabled}
                onClick={() =>
                  onChange(
                    shortDurationSelected
                      ? withoutDuration(value)
                      : { ...value, duration_min: 0, duration_max: 3 },
                  )
                }
              />
            </div>
            {/* 고정 범위는 히스토그램 경계와 다를 수 있어 슬라이더로 표시하지 않는다.
                토글을 해제하면 구간 선택으로 돌아간다. */}
            {!shortDurationSelected && facets !== null && facets.duration.bins.length > 0 && (
              <div className={styles.wide}>
                <EpisodeDurationHistogram
                  duration={facets.duration}
                  value={value}
                  onChange={onChange}
                  disabled={disabled}
                />
              </div>
            )}
            {/* 밴드는 축마다 드롭다운이 하나다. 서버에서 두 축은 독립이고, 함께 적용하면 AND 다.
                구간 문구(`크게 다름 (≥45%)`)가 반 칸에서 잘리므로 축마다 한 줄을 쓴다. */}
            {episodeBandFilterAxes(value, facets).map((control) => (
              <div key={control.axis} className={styles.wide}>
                <SingleSelectDropdown
                  mode={hatcheryDropdownModes.filter}
                  label={control.label}
                  fullWidth
                  neutralValue={EPISODE_FILTER_ALL}
                  options={control.options as readonly SingleSelectDropdownOption[]}
                  value={control.value}
                  disabled={disabled}
                  onValueChange={(next) => onChange(applyBandAxisValue(value, control.axis, next))}
                />
              </div>
            ))}
            {bandNote !== null && <p className={`${styles.wide} ${styles.axisNote}`}>{bandNote}</p>}
            <div className={styles.wide}>
              <SingleSelectDropdown
                mode={hatcheryDropdownModes.filter}
                label={BAND_CHANNEL_AXIS_LABEL}
                fullWidth
                searchable
                searchPlaceholder="채널 검색"
                neutralValue={EPISODE_FILTER_ALL}
                options={
                  episodeWorstChannelOptions(value, facets) as readonly SingleSelectDropdownOption[]
                }
                value={readEpisodeFilterAxis(value, "band_worst_channel")}
                disabled={disabled}
                onValueChange={(next) =>
                  onChange(applyEpisodeFilterAxis(value, "band_worst_channel", next))
                }
              />
            </div>
          </div>
        </AnchoredPopup.Body>
        <AnchoredPopup.Footer align="end">
          {/* 칩 행의 초기화와 뜻이 같다. 이 목록에 적용된 조건 전부(이름 검색 포함)를 비운다.
              두 버튼의 범위가 다르면 어느 버튼을 눌렀는지에 따라 결과가 달라진다. */}
          <IconButton
            text="전체 해제"
            type="button"
            size="sm"
            variant="ghost"
            disabled={disabled || !hasAnyFilter}
            onClick={() => onChange({})}
          >
            <RotateCcwIcon />
          </IconButton>
          <IconButton text="완료" size="sm" variant="solid" onClick={closeSheet} />
        </AnchoredPopup.Footer>
      </AnchoredPopup>
    </>
  );
}

/**
 * 축별 선택지 개수. **목록과 같은 필터**로 받는 조건부 수다.
 * 결함 축(갈래·값)은 어휘도 이 응답에서 받는다.
 *
 * 마운트할 때 현재 조건으로 1회 미리 받는다. 목록 조회와 병렬이므로 왕복이 순차로 쌓이지 않는다.
 * 팝오버를 열 때 숫자가 이미 있어야 사용자가 `(130)` 을 "아직 안 온 값"으로 잘못 읽지 않는다.
 * 그 뒤에는 **수가 보이는 동안만** 다시 받는다(`useAppliedFacetQuery`).
 *
 * 실패하면 직전 값을 유지하고(처음이면 `null`) 오류를 무시한다. 수만 빠지고 축은 그대로 표시한다.
 * 목록은 이 응답 없이 동작해야 하므로 호출부에 알려도 할 수 있는 일이 없다.
 */
function useEpisodeFacets(
  datasetUuid: string,
  filters: Filters,
  open: boolean,
): EpisodeFacets | null {
  const [facets, setFacets] = useState<EpisodeFacets | null>(null);
  const applied = useAppliedFacetQuery(datasetUuid, filters, open);

  // 데이터셋이 바뀌면 이전 수를 즉시 버린다. 다른 데이터셋의 수를 한 프레임도 표시하면 안 된다.
  // 조건만 바뀐 재조회에서는 수를 비우지 않는다. 그래서 열린 팝오버의 숫자는 깜빡이지 않고
  // 제자리에서 바뀐다.
  useEffect(() => {
    setFacets(null);
  }, [datasetUuid]);

  useEffect(() => {
    let cancelled = false;
    void getDatasetEpisodeFacets(datasetUuid, applied.filters)
      .then((next) => {
        if (!cancelled) {
          setFacets(next);
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [datasetUuid, applied]);
  return facets;
}

type AppliedFacetQuery = { filters: Filters; key: string; saved: number };

/**
 * 개수를 받을 조건. 현재 필터와 리뷰 저장 세대를 **팝오버가 열려 있는 동안에만** 적용 값으로 올린다.
 *
 * 수는 팝오버 안에만 보인다. 닫힌 상태에서 조건을 바꾸거나 리뷰를 저장해도(`Enter`·`S` 반복 흐름)
 * 그 숫자는 화면에 없으므로 다시 받지 않는다. 다음에 팝오버를 열 때 한 번에 반영한다.
 * 열려 있는 동안에는 다음과 같이 받는다:
 *   - 저장이 들어오면 즉시 받는다.
 *   - 조건이 바뀌면 debounce 뒤에 받는다. 다중 선택을 연달아 체크하는 동안 요청이 체크 수만큼
 *     늘지 않게 한다.
 */
function useAppliedFacetQuery(
  datasetUuid: string,
  filters: Filters,
  open: boolean,
): AppliedFacetQuery {
  const saved = useEpisodeReviewSavedRevision(datasetUuid);
  const key = facetQueryKey(filters);
  const [applied, setApplied] = useState<AppliedFacetQuery>(() => ({ filters, key, saved }));
  const latestFilters = useRef(filters);
  latestFilters.current = filters;
  const wasOpen = useRef(open);

  useEffect(() => {
    const justOpened = open && !wasOpen.current;
    wasOpen.current = open;
    if (!open || (applied.key === key && applied.saved === saved)) {
      return;
    }
    const next = { filters: latestFilters.current, key, saved };
    if (justOpened || applied.saved !== saved) {
      setApplied(next);
      return;
    }
    const timer = window.setTimeout(() => setApplied(next), FACETS_REFETCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [open, key, saved, applied]);

  return applied;
}

// 개수를 바꾸는 조건만으로 만든 비교 키. 정렬은 수를 바꾸지 않으므로 뺀다.
function facetQueryKey(filters: Filters): string {
  return EPISODE_LIST_FILTER_KEYS.filter((name) => name !== "sort" && name !== "order")
    .map((name) => String(filters[name] ?? ""))
    .join("|");
}

/**
 * 결함 갈래 소속(code → 값). 드롭다운이 갈래 행 아래에 값을 묶을 때만 쓴다.
 * 칩은 소속이 필요 없으므로 팝오버를 처음 열 때 받는다. 세션 캐시라 두 번째부터는 왕복이 없다.
 * 받지 못하면 null 이다. 그러면 갈래 행은 표시하지만 값은 묶지 않는다.
 */
function useQualityMembership(open: boolean): ReadonlyMap<string, readonly string[]> | null {
  const [requested, setRequested] = useState(open);
  useEffect(() => {
    if (open) {
      setRequested(true);
    }
  }, [open]);
  const categories = useLabelCategories("quality", requested);
  if (categories.length === 0) {
    return null;
  }
  return new Map(categories.map((category) => [category.code, category.values] as const));
}
